import React from "react";
import { Box, SxProps, Theme } from "@mui/material";
import CustomSelect, { ICustomSelectProps } from "./common/CustomSelect";
import CustomTextField, {
  ICustomTextFieldProps,
} from "./common/CustomTextField";
import CustomSwitch, { CustomSwitchProps } from "./common/CustomSwitch";

type RateTemplateFormProps = {
  textFields?: (ICustomTextFieldProps & { sx?: SxProps<Theme> })[];
  dropdowns?: (ICustomSelectProps & { sx?: SxProps<Theme> })[];
  switches?: (CustomSwitchProps & { sx?: SxProps<Theme> })[];
  buttons?: React.ReactNode[];
  additionalComponents?: React.ReactNode[];
  sx?: SxProps<Theme>;
  gap?: number | string;
  layout?: "row" | "column";
  flexBasis?: {
    textField?: string;
    dropdown?: string;
    switch?: string;
  };
};

const 
RateTemplateForm: React.FC<RateTemplateFormProps> = ({
  textFields = [],
  dropdowns = [],
  switches = [],
  buttons = [],
  additionalComponents = [],
  sx,
  gap = 3,
  layout = "row",
  flexBasis = {
    textField: "45%",
    dropdown: "45%",
    switch: "45%",
  },
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: layout === "row" ? { xs: "column", sm: "row" } : "column",
        flexWrap: "wrap",
        gap: gap,
        backgroundColor: "#fff",
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        width: '100%',
        padding: { xs: 1, sm: 2, md: 4 },
        ...sx,
        alignItems: layout === "row" ? { xs: "stretch", sm: "flex-start" } : undefined,
      }}
    >
      {textFields.map((field, index) => (
        <Box
          key={`textfield-${index}`}
          sx={{
            flexBasis: { xs: "100%", sm: "48%", md: flexBasis.textField },
            flexGrow: 1,
            minWidth: 0,
            ...field.sx,
          }}
        >
          <CustomTextField {...field} />
        </Box>
      ))}

      {dropdowns.map((dropdownProps, index) => (
        <Box
          key={`dropdown-${index}`}
          sx={{
            flexBasis: { xs: "100%", sm: "48%", md: flexBasis.dropdown },
            flexGrow: 1,
            minWidth: 0,
            ...dropdownProps.sx,
          }}
        >
          <CustomSelect {...dropdownProps} />
        </Box>
      ))}

      {switches.map((switchProps, index) => (
        <Box
          key={`switch-${index}`}
          sx={{
            flexBasis: { xs: "100%", sm: "48%", md: flexBasis.switch },
            flexGrow: 1,
            minWidth: 0,
            ...switchProps.sx,
          }}
        >
          <CustomSwitch {...switchProps} />
        </Box>
      ))}

      {additionalComponents.map((component, index) => (
        <Box
          key={`component-${index}`}
          sx={{
            flexBasis: { xs: "100%", sm: "100%", md: "100%" },
            marginTop: { xs: 2, sm: 3 },
          }}
        >
          {component}
        </Box>
      ))}

      {buttons.length > 0 && (
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent={{ xs: "center", sm: "flex-end" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={2}
          sx={{
            flexBasis: "100%",
            mt: { xs: 2, sm: 3 },
          }}
        >
          {buttons.map((button, index) => (
            <Box key={`button-${index}`} sx={{ minWidth: 120 }}>
              {button}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RateTemplateForm;
